import React, { createContext, useState, useContext, useEffect } from "react";
import { useAuth } from "../AuthContext";

// Create context
export const NotificationContext = createContext();

// Custom hook to use the notification context
export const useNotifications = () => useContext(NotificationContext);

/**
 * NotificationProvider component that provides notification functionality to the app
 */
export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [popupNotification, setPopupNotification] = useState(null);

  // Clear popup notification
  const clearPopupNotification = () => {
    setPopupNotification(null);
  };

  // Load notifications when user logs in or changes
  useEffect(() => {
    if (user) {
      // Load relevant notifications based on user role
      loadRelevantNotifications(user.role);
    } else {
      // Clear notifications when user logs out
      setNotifications([]);
    }
  }, [user]);

  /**
   * Load notifications based on user role
   * In a real app, this would fetch from an API
   */
  const loadRelevantNotifications = (role) => {
    let demoNotifications = [];

    switch (role) {
      case "company":
        demoNotifications = [
          {
            id: 1,
            title: "New Application Received via email",
            message:
              "Sarah Johnson applied for the UI/UX Design Intern position.",
            read: false,
            timestamp: new Date(
              new Date().getTime() - 30 * 60000
            ).toISOString(),
            type: "application",
            forRole: "company",
            data: { applicationId: "app123" },
          },
          {
            id: 2,
            title: "Application Status Updated via email",
            message:
              "Your job posting for Backend Developer has been approved by SCAD Office.",
            read: true,
            timestamp: new Date(
              new Date().getTime() - 120 * 60000
            ).toISOString(),
            type: "status",
            forRole: "company",
            data: { jobPostId: "job456" },
          },
        ];
        break;

      case "scadOffice":
        demoNotifications = [
          {
            id: 3,
            title: "Appointment Accepted",
            message:
              "Student Ahmed has accepted your appointment scheduled for May 15, 2025.",
            read: false,
            timestamp: new Date(
              new Date().getTime() - 45 * 60000
            ).toISOString(),
            type: "appointment",
            forRole: "scadOffice",
            data: { appointmentId: "apt789" },
          },
          {
            id: 4,
            title: "New Company Registration",
            message: "TechCorp has registered and is awaiting approval.",
            read: true,
            timestamp: new Date(
              new Date().getTime() - 8 * 60 * 60000
            ).toISOString(),
            type: "registration",
            forRole: "scadOffice",
            data: { companyId: "comp234" },
          },
        ];
        break;

      case "proStudent":
        demoNotifications = [
          {
            id: 5,
            title: "New Internship Cycle",
            message:
              "Summer 2025 internship cycle has begun ends in June. Start applying now!",
            read: false,
            timestamp: new Date(
              new Date().getTime() - 12 * 60 * 60000
            ).toISOString(),
            type: "cycle",
            forRole: "proStudent",
            data: { cycleId: "cyc345" },
          },
          {
            id: 6,
            title: "Report Status Updated",
            message:
              "Your internship report has been approved by your academic supervisor.",
            read: false,
            timestamp: new Date(
              new Date().getTime() - 3 * 60 * 60000
            ).toISOString(),
            type: "report",
            forRole: "proStudent",
            data: { reportId: "rep567" },
          },
          {
            id: 7,
            title: "Upcoming Workshop",
            message: 'Reminder: "Resume Building Workshop" starts in 2 days.',
            read: true,
            timestamp: new Date(
              new Date().getTime() - 24 * 60 * 60000
            ).toISOString(),
            type: "workshop",
            forRole: "proStudent",
            data: { workshopId: "wks678" },
          },
          {
            id: 8,
            title: "New Workshop Message",
            message:
              'Linda posted: "Will we receive certificates after completion?"',
            read: false,
            timestamp: new Date(
              new Date().getTime() - 2 * 60 * 60000
            ).toISOString(),
            type: "message",
            forRole: "proStudent",
            data: { workshopId: "wks678", messageId: "msg123" },
          },
          {
            id: 9,
            title: "Appointment Confirmed",
            message:
              "SCAD Officer has accepted your appointment request for May 18, 2025.",
            read: true,
            timestamp: new Date(
              new Date().getTime() - 50 * 60000
            ).toISOString(),
            type: "appointment",
            forRole: "proStudent",
            data: { appointmentId: "apt890" },
          },
        ];
        break;

      case "student":
        demoNotifications = [
          {
            id: 10,
            title: "New Internship Cycle",
            message:
              "Summer 2025 internship cycle has begun ends in June. Start applying now!",
            read: false,
            timestamp: new Date(
              new Date().getTime() - 12 * 60 * 60000
            ).toISOString(),
            type: "cycle",
            forRole: "student",
            data: { cycleId: "cyc345" },
          },
          {
            id: 11,
            title: "Report Status Updated",
            message:
              "Your internship report has been approved by your academic supervisor.",
            read: true,
            timestamp: new Date(
              new Date().getTime() - 5 * 60 * 60000
            ).toISOString(),
            type: "report",
            forRole: "student",
            data: { reportId: "rep901" },
          },
        ];
        break;

      default:
        demoNotifications = [];
    }

    setNotifications(demoNotifications);
  };
  /**
   * Add a new notification with role checking
   */
  const addNotification = (notification, sendEmail = false) => {
    // Check if it's a component-based notification
    if (notification.component) {
      // Show component notification directly
      console.log("Adding component notification:", notification);
      const NotificationComponent = notification.component;
      setPopupNotification({
        id: Date.now(),
        component: NotificationComponent,
        props: notification.props || {},
      });
      return null;
    }

    // Validate if the notification is meant for the current user's role
    if (user && notification.forRole && notification.forRole !== user.role) {
      console.warn(
        `Notification not added: Target role ${notification.forRole} doesn't match current user role ${user.role}`
      );
      return null;
    }

    const newNotification = {
      id: Date.now(),
      read: false,
      timestamp: new Date().toISOString(),
      ...notification,
    };

    setNotifications((prev) => [newNotification, ...prev]);

    // Send an email if required
    if (sendEmail && user?.email) {
      sendEmailNotification(user.email, newNotification);
    }

    return newNotification.id;
  };

  /**
   * Send an email notification (mock implementation)
   */
  const sendEmailNotification = (email, notification) => {
    console.log(`Sending email to ${email}: ${notification.title}`);
    // In a real app, this would call an API to send an email
  };

  /**
   * Add a notification specifically for company users
   */
  const addCompanyNotification = (notification) => {
    return addNotification({
      ...notification,
      forRole: "company",
    });
  };

  /**
   * Add a notification specifically for SCAD office users
   */
  const addScadOfficeNotification = (notification) => {
    return addNotification({
      ...notification,
      forRole: "scadOffice",
    });
  };

  /**
   * Add a notification specifically for pro student users
   */
  const addProStudentNotification = (notification) => {
    return addNotification({
      ...notification,
      forRole: "proStudent",
    });
  };

  /**
   * Add a notification specifically for student users
   */
  const addStudentNotification = (notification) => {
    return addNotification({
      ...notification,
      forRole: "student",
    });
  };

  /**
   * Notify about application status change
   */
  const notifyApplicationStatusChange = (application, status) => {
    if (application.studentId) {
      addStudentNotification({
        title: "Application Status Updated",
        message: `Your application for ${
          application.position
        } has been ${status.toLowerCase()}.`,
        type: "status",
        data: { applicationId: application.id },
      });
    }
  };

  /**
   * Notify about new application
   */
  const notifyNewApplication = (application) => {
    if (application.companyId) {
      addCompanyNotification({
        title: "New Application Received",
        message: `${application.studentName} applied for the ${application.position} position.`,
        type: "application",
        data: { applicationId: application.id },
      });
    }
  };

  /**
   * Notify about appointment acceptance
   */
  const notifyAppointmentAccepted = (appointment) => {
    if (appointment.scadOfficeId) {
      addScadOfficeNotification({
        title: "Appointment Accepted",
        message: `Student ${appointment.studentName} has accepted your appointment scheduled for ${appointment.date}.`,
        type: "appointment",
        data: { appointmentId: appointment.id },
      });
    }
  };

  /**
   * Notify about internship cycle
   */
  const notifyInternshipCycle = (cycle, isUpcoming = false) => {
    const title = isUpcoming
      ? "Upcoming Internship Cycle"
      : "New Internship Cycle";

    const message = isUpcoming
      ? `The ${cycle.name} internship cycle starts in ${cycle.daysUntilStart} days.`
      : `The ${cycle.name} internship cycle has begun and ends on ${cycle.endDate}. Start applying now!`;

    // Notify both student types
    addStudentNotification({
      title,
      message,
      type: "cycle",
      data: { cycleId: cycle.id },
    });

    addProStudentNotification({
      title,
      message,
      type: "cycle",
      data: { cycleId: cycle.id },
    });
  };

  /**
   * Notify about report status
   */
  const notifyReportStatus = (report) => {
    const status = report.status.toLowerCase();
    const title = `Report Status Updated`;
    let message = "";

    if (status === "approved") {
      message = `Your internship report has been approved by your academic supervisor.`;
    } else if (status === "rejected") {
      message = `Your internship report has been rejected. ${
        report.feedback || ""
      }`;
    } else if (status === "pending") {
      message = `Your internship report is now under review by your academic supervisor.`;
    }

    if (report.studentType === "proStudent") {
      addProStudentNotification({
        title,
        message,
        type: "report",
        data: { reportId: report.id },
      });
    } else {
      addStudentNotification({
        title,
        message,
        type: "report",
        data: { reportId: report.id },
      });
    }
  };

  /**
   * Notify about upcoming workshop
   */
  const notifyUpcomingWorkshop = (workshop) => {
    addProStudentNotification({
      title: "Upcoming Workshop",
      message: `Reminder: "${workshop.title}" starts in ${workshop.daysUntilStart} days.`,
      type: "workshop",
      data: { workshopId: workshop.id },
    });
  };

  /**
   * Notify about workshop message
   */
  const notifyWorkshopMessage = (message, workshop) => {
    addProStudentNotification({
      title: "New Workshop Message",
      message: `${message.author} posted: "${message.content}"`,
      type: "message",
      data: { workshopId: workshop.id, messageId: message.id },
    });
  };

  /**
   * Notify about student appointment acceptance
   */
  const notifyStudentAppointmentAccepted = (appointment) => {
    if (appointment.studentId) {
      addStudentNotification({
        title: "Appointment Confirmed",
        message: `SCAD Officer has accepted your appointment request for ${appointment.date}.`,
        type: "appointment",
        data: { appointmentId: appointment.id },
      });
    }
  };

  /**
   * Mark a notification as read
   */
  const markAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true }))
    );
  };

  /**
   * Delete a notification
   */
  const deleteNotification = (notificationId) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== notificationId)
    );
  };

  /**
   * Delete all notifications related to a workshop
   */
  const deleteWorkshopNotifications = (workshopId) => {
    setNotifications((prev) =>
      prev.filter(
        (notification) => !(notification.data?.workshopId === workshopId)
      )
    );
  };

  /**
   * Get the count of unread notifications
   */
  const getUnreadCount = () => {
    return notifications.filter((notification) => !notification.read).length;
  };

  // Context value
  const value = {
    notifications,
    popupNotification,
    clearPopupNotification,
    addNotification,
    addCompanyNotification,
    addScadOfficeNotification,
    addProStudentNotification,
    addStudentNotification,
    notifyApplicationStatusChange,
    notifyNewApplication,
    notifyAppointmentAccepted,
    notifyInternshipCycle,
    notifyReportStatus,
    notifyUpcomingWorkshop,
    notifyWorkshopMessage,
    notifyStudentAppointmentAccepted,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteWorkshopNotifications,
    getUnreadCount,
  };
  return (
    <NotificationContext.Provider value={value}>
      {children}
      {popupNotification && (
        <div 
          className="notification-popup" 
          style={{ 
            zIndex: 9999,
            padding: '15px',
            margin: '10px'
          }}
        >
          {React.createElement(popupNotification.component, {
            ...popupNotification.props,
            onClose: clearPopupNotification,
            key: `popup-${popupNotification.id}`,
          })}
        </div>
      )}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
